/**
 * Vista previa de los libros de una devolución (pedido del usuario del 2026-10-30), antes de «Registrar devolución»: la
 * fila de la nota de crédito en el libro de ventas (resta, con la fecha de hoy) y lo que entra a los libros de inventario
 * real y fiscal. La cuenta la hace `vistaDevolucionLibros` (`packages/shared/src/vista-libros.ts`) con el cálculo del
 * servidor; que los libros digan lo mismo lo prueba `apps/api/test/bugs/vista-libros-devolucion.spec.ts`.
 */
import { diaLegible, formatearCantidad, Money, vistaDevolucionLibros, type CalculoDevolucionDTO, type OrigenDevolucionDTO } from '@mg/shared';
import { BsLibro, FilaLibro, hoyCaracas, mesDeLaFecha, TarjetaVistaLibros, TituloLibro } from '@/componentes/VistaPreviaLibros.tsx';
import { Badge } from '@/componentes/ui/badge.tsx';

export function VistaDevolucion({ calculo, origen, veLibroVentas, veInventario, conFacturaNueva = false, hoy = hoyCaracas(), testid = 'dev-vista-libros' }: {
  calculo: CalculoDevolucionDTO;
  origen: Pick<OrigenDevolucionDTO, 'numero_formateado' | 'cliente_rif' | 'cliente_razon_social'>;
  /** Quien ve los libros de ventas y compras (`libros.ver`). */
  veLibroVentas: boolean;
  /** Quien ve costos (dueña, contadora): el libro de inventario lleva costos. */
  veInventario: boolean;
  /** La devolución despacha una factura nueva: sale aparte, sumando. */
  conFacturaNueva?: boolean;
  hoy?: string;
  testid?: string;
}) {
  if (!veLibroVentas && !veInventario) return null;
  const v = vistaDevolucionLibros(calculo);
  const mes = mesDeLaFecha(hoy);
  const conRetencion = Money.parse(calculo.retencion_iva).esPositivo() || Money.parse(calculo.retencion_municipal).esPositivo();
  return (
    <TarjetaVistaLibros testid={testid}>
      {veLibroVentas ? (
        <div className="space-y-0.5" data-testid={`${testid}-ventas`}>
          {v.libro_ventas ? (
            <>
              <TituloLibro titulo={`Libro de ventas de ${mes}`} testid={`${testid}-mes`} nota="La nota de crédito resta. Sale con la fecha de hoy, aunque la factura sea de otro mes." />
              <FilaLibro etiqueta="Fecha">{diaLegible(hoy)}</FilaLibro>
              <FilaLibro etiqueta="RIF">{origen.cliente_rif}</FilaLibro>
              <FilaLibro etiqueta="Razón social">{origen.cliente_razon_social}</FilaLibro>
              <FilaLibro etiqueta="Tipo" testid={`${testid}-tipo`}>NC · ref. {origen.numero_formateado}</FilaLibro>
              <FilaLibro etiqueta="N° doc y control">los siguientes, al registrarla</FilaLibro>
              <FilaLibro etiqueta="Total con IVA" testid={`${testid}-total`}><BsLibro valor={v.libro_ventas.total_con_iva} /></FilaLibro>
              <FilaLibro etiqueta="Exento" testid={`${testid}-exento`}><BsLibro valor={v.libro_ventas.exento} /></FilaLibro>
              <FilaLibro etiqueta="Base" testid={`${testid}-base`}><BsLibro valor={v.libro_ventas.base} /></FilaLibro>
              <FilaLibro etiqueta="IVA" testid={`${testid}-iva`}><BsLibro valor={v.libro_ventas.iva} /></FilaLibro>
              <FilaLibro etiqueta="IGTF" testid={`${testid}-igtf`}><BsLibro valor={v.libro_ventas.igtf} /></FilaLibro>
              <FilaLibro etiqueta="IVA retenido" testid={`${testid}-ret-iva`}><BsLibro valor={v.libro_ventas.iva_retenido} /></FilaLibro>
              <FilaLibro etiqueta="Ret. municipal" testid={`${testid}-ret-mun`}><BsLibro valor={v.libro_ventas.retencion_municipal} /></FilaLibro>
              <p className="text-[11px] text-muted-foreground">La nota de crédito no lleva IGTF{conRetencion ? ' ni cambia la retención de la factura' : ''}.{conFacturaNueva ? ' La factura nueva del despacho sale aparte, sumando.' : ''}</p>
            </>
          ) : (
            <TituloLibro titulo="Libro de ventas" testid={`${testid}-mes`} nota="El «Cambio» no entra al libro de ventas: no es un documento fiscal." />
          )}
        </div>
      ) : null}
      {veInventario ? (
        <div className={`space-y-0.5 ${veLibroVentas ? 'border-t pt-2' : ''}`} data-testid={`${testid}-inventario`}>
          <TituloLibro titulo={`Libro de inventario de ${mes}`} nota="Entra hoy, al costo promedio del mes." />
          {v.inventario_real.length === 0 ? <p className="text-[11px] text-muted-foreground">No cambia: lo devuelto no lleva existencia (servicios o piezas de vidrio).</p> : null}
          {v.inventario_real.map((e, i) => (
            <p key={`r${i}`} className="flex justify-between gap-3" data-testid={`${testid}-real-${e.codigo}-${e.columna}`}>
              <span className="min-w-0 break-words">{e.codigo} · {e.nombre}<span className="block text-[11px] text-muted-foreground">{e.columna === 'entradas' ? 'Entradas' : 'Retiros'} · {e.concepto} <Badge variant="neutro" className="ml-1">real</Badge></span></span>
              <span className="num shrink-0 text-right">{e.columna === 'entradas' ? '+' : '−'}{formatearCantidad(Money.parse(e.unidades))}</span>
            </p>
          ))}
          {v.inventario_fiscal.map((e, i) => (
            <p key={`f${i}`} className="flex justify-between gap-3" data-testid={`${testid}-fiscal-${e.codigo}`}>
              <span className="min-w-0 break-words">{e.codigo} · {e.nombre}<span className="block text-[11px] text-muted-foreground">Entradas · {e.concepto} <Badge variant="info" className="ml-1">fiscal</Badge></span></span>
              <span className="num shrink-0 text-right">+{formatearCantidad(Money.parse(e.unidades))}</span>
            </p>
          ))}
          {v.inventario_real.some((e) => e.columna === 'retiros') ? <p className="text-[11px] text-muted-foreground">Lo dañado entra y sale en el acto en el real; en el fiscal entra completo con la nota de crédito.</p> : null}
          {!v.libro_ventas && v.inventario_real.length > 0 ? <p className="text-[11px] text-muted-foreground">Al libro de inventario fiscal no entra: el «Cambio» no es fiscal.</p> : null}
        </div>
      ) : null}
    </TarjetaVistaLibros>
  );
}
